// Median height-for-age in inches (sex-averaged, approximate CDC/WHO medians).
const GROWTH_TABLE_INCHES: Record<number, number> = {
  1: 30, 2: 35, 3: 38, 4: 41, 5: 43, 6: 46, 7: 48, 8: 51, 9: 53,
  10: 55, 11: 57, 12: 60, 13: 62, 14: 64, 15: 66, 16: 67, 17: 68, 18: 68,
};
const ADULT_DEFAULT_INCHES = 67;
const ADULT_BAND: [number, number] = [60, 78];

export function parseHeightToInches(heightStr: string): number {
  if (!heightStr) return 0;
  const feetMatch = heightStr.match(/(\d+)\s*(?:feet|ft|')/i);
  const inchesMatch = heightStr.match(/(\d+)\s*(?:inches|in|")/i);
  const feet = feetMatch ? parseInt(feetMatch[1], 10) : 0;
  const inches = inchesMatch ? parseInt(inchesMatch[1], 10) : 0;
  return feet * 12 + inches;
}

function bandForAge(age: number): [number, number] {
  if (!age || age >= 18) return ADULT_BAND;
  const median = GROWTH_TABLE_INCHES[Math.max(1, Math.min(18, Math.round(age)))];
  return [Math.max(24, median - 6), median + 8];
}

// Gender height delta vs the sex-averaged median, in inches. Negligible for young children, growing
// to ±3" by adulthood (boy/man taller, girl/woman shorter; neutral/unknown = 0).
function genderedAdjustInches(age: number, gender?: string): number {
  if (!gender) return 0;
  const g = gender.toLowerCase();
  const dir = /\b(boy|man|male)\b/.test(g) ? 1 : /\b(girl|woman|female)\b/.test(g) ? -1 : 0;
  if (dir === 0) return 0;
  const a = !age ? 18 : age;
  const scale = Math.min(1, Math.max(0, (a - 8) / 10)); // 0 at age<=8, 1 at age>=18
  return dir * Math.round(3 * scale);
}

export function ageToHeightInches(age: number, aiHeightStr?: string, gender?: string): number {
  const base = (!age || age >= 18)
    ? ADULT_DEFAULT_INCHES
    : GROWTH_TABLE_INCHES[Math.max(1, Math.min(18, Math.round(age)))];
  const [low, high] = bandForAge(age);
  if (aiHeightStr) {
    const parsed = parseHeightToInches(aiHeightStr);
    if (parsed >= low && parsed <= high) return parsed;
  }
  const median = base + genderedAdjustInches(age, gender);
  return Math.max(low, Math.min(high, median));
}

// Format inches back to a human/prompt-friendly height string, e.g. 67 -> 5'7", 60 -> 5', 38 -> 3'2".
export function formatInchesToHeight(inches: number): string {
  const n = Math.max(1, Math.round(inches));
  const ft = Math.floor(n / 12);
  const inch = n % 12;
  return inch ? `${ft}'${inch}"` : `${ft}'`;
}

export function cmToInches(cm: number): number { return Math.round(cm / 2.54); }
export function inchesToCm(inches: number): number { return Math.round(inches * 2.54); }

// The one "stored-wins-else-derive" resolver every consumer uses.
export function resolveHeightInches(c: { heightInches?: number; age?: number; height?: string; gender?: string }): number {
  if (typeof c.heightInches === 'number' && Number.isFinite(c.heightInches) && c.heightInches >= 12 && c.heightInches <= 96) {
    return Math.round(c.heightInches);
  }
  return ageToHeightInches(c.age ?? 0, c.height, c.gender);
}

export function parseObjectHeightInches(description: string): number {
  if (!description) return 0;
  const m = description.match(/(\d+(?:\.\d+)?)\s*(feet|foot|ft|inches|inch|in)\b/i);
  if (!m) return 0;
  const value = parseFloat(m[1]);
  const unit = m[2].toLowerCase();
  const inches = unit.startsWith('f') ? value * 12 : value;
  return Math.max(1, Math.min(1200, Math.round(inches)));
}
